import { useCallback, useEffect, useMemo, useState } from "react";
import { AnimatePresence, motion, useReducedMotion } from "motion/react";
import {
  ChevronUp,
  CircleCheck,
  CircleDot,
  Clock3,
  CreditCard,
  Hourglass,
  ReceiptText,
  RefreshCw,
  Send,
  ShoppingCart,
  WifiOff,
  X
} from "lucide-react";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import useCustomerSocket from "../hooks/useCustomerSocket";
import "./SessionTracker.css";

const STATUS_LABELS = {
  pending: "Enviada",
  accepted: "Aceptada",
  preparing: "Preparando",
  served: "Servida",
  paid: "Pagada",
  cancelled: "Cancelada"
};

const TIMELINE = [
  { key: "pending", label: "Enviada", field: "createdAt" },
  { key: "accepted", label: "Acept.", field: "acceptedAt" },
  { key: "preparing", label: "Prep.", field: "preparingAt" },
  { key: "served", label: "Servida", field: "servedAt" },
  { key: "paid", label: "Pagada", field: "paidAt" }
];

const STATUS_INDEX = {
  pending: 0,
  accepted: 1,
  preparing: 2,
  served: 3,
  paid: 4,
  cancelled: 0
};

const PAYMENT_LABELS = {
  cash: "Efectivo",
  card: "Tarjeta",
  bizum: "Bizum",
  transfer: "Transferencia",
  other: "Otro"
};

function money(value) {
  const number = Number(value || 0);
  return `${number.toFixed(2)} €`;
}

function asArray(value) {
  return Array.isArray(value) ? value : [];
}

function dateValue(value) {
  const date = value ? new Date(value) : null;
  return date && !Number.isNaN(date.getTime()) ? date : null;
}

function timeLabel(value) {
  const date = dateValue(value);
  if (!date) return "";
  return date.toLocaleTimeString("es-ES", { hour: "2-digit", minute: "2-digit" });
}

function minutesAgo(value) {
  const date = dateValue(value);
  if (!date) return "ahora";
  const minutes = Math.max(0, Math.round((Date.now() - date.getTime()) / 60000));
  if (minutes < 1) return "ahora";
  if (minutes === 1) return "hace 1 min";
  if (minutes < 90) return `hace ${minutes} min`;
  const hours = Math.floor(minutes / 60);
  return `hace ${hours} h`;
}

function getOrderId(order) {
  return String(order?.orderId || order?._id || "");
}

function getInvoiceId(invoice) {
  return String(invoice?.invoiceId || invoice?._id || "");
}

function normalizeOrder(order) {
  return {
    ...order,
    _id: getOrderId(order),
    orderId: getOrderId(order),
    items: asArray(order?.items)
  };
}

function normalizeInvoice(invoice) {
  return {
    ...invoice,
    _id: getInvoiceId(invoice),
    invoiceId: getInvoiceId(invoice),
    items: asArray(invoice?.items)
  };
}

function buildSummary(orders, invoices, fallback = {}) {
  const safeOrders = asArray(orders);
  const safeInvoices = asArray(invoices).filter((invoice) => invoice.status !== "cancelled");
  const totalOrdered = safeOrders
    .filter((order) => order.status !== "cancelled")
    .reduce((sum, order) => sum + Number(order.total || 0), 0);
  const totalInvoiced = safeInvoices.reduce((sum, invoice) => sum + Number(invoice.total || 0), 0);

  return {
    ...fallback,
    totalOrdered,
    totalInvoiced,
    ordersLive: safeOrders.filter((order) => !["paid", "cancelled"].includes(order.status)).length,
    ordersTotal: safeOrders.length
  };
}

function upsertById(items, nextItem, getId) {
  const id = getId(nextItem);
  if (!id) return items;
  const found = items.some((item) => getId(item) === id);
  const next = found
    ? items.map((item) => (getId(item) === id ? { ...item, ...nextItem } : item))
    : [...items, nextItem];
  return next.sort((a, b) => new Date(a.createdAt || a.date || 0) - new Date(b.createdAt || b.date || 0));
}

function StatusBadge({ status }) {
  const label = STATUS_LABELS[status] || STATUS_LABELS.pending;
  return <span className={`st-status st-status-${status || "pending"}`}>{label}</span>;
}

function Timeline({ order }) {
  const current = STATUS_INDEX[order.status] ?? 0;
  const cancelled = order.status === "cancelled";

  return (
    <div className={`st-order-timeline${cancelled ? " is-cancelled" : ""}`} aria-label="Estado del pedido">
      {TIMELINE.map((step, index) => {
        const done = !cancelled && index < current;
        const currentStep = !cancelled && index === current;
        const active = done || currentStep;
        return (
          <div
            key={step.key}
            className={`st-order-timeline-step${done ? " is-done" : ""}${currentStep ? " is-current" : ""}`}
          >
            <span className="st-dot" aria-hidden="true" />
            <span className="st-step-label">{step.label}</span>
            <span className="st-step-time">{active ? timeLabel(order[step.field]) : ""}</span>
          </div>
        );
      })}
    </div>
  );
}

function OrderTimelineCard({ order, index }) {
  const reduce = useReducedMotion();
  const items = asArray(order.items);

  return (
    <motion.article
      className={`st-order-card st-order-${order.status || "pending"}`}
      initial={reduce ? false : { opacity: 0, y: 10, scale: 0.98 }}
      animate={reduce ? undefined : { opacity: 1, y: 0, scale: 1 }}
      transition={{ duration: 0.22, delay: Math.min(index * 0.035, 0.18) }}
      layout
    >
      <header className="st-order-head">
        <div>
          <p>Pedido · {timeLabel(order.createdAt)}</p>
          <h4>{items.length} productos</h4>
        </div>
        <div className="st-order-meta">
          <StatusBadge status={order.status} />
          <strong>{money(order.total)}</strong>
        </div>
      </header>

      <ul className="st-order-items">
        {items.map((item, itemIndex) => (
          <li key={`${item.nameSnapshot}-${itemIndex}`}>
            <span>{Number(item.quantity || 0).toLocaleString("es-ES")}x {item.nameSnapshot}</span>
            {item.status && <em>{STATUS_LABELS[item.status] || item.status}</em>}
          </li>
        ))}
      </ul>

      <Timeline order={order} />
    </motion.article>
  );
}

function InvoiceTicket({ invoice, index }) {
  const reduce = useReducedMotion();
  const method = invoice.paymentMethodLabel || PAYMENT_LABELS[invoice.paymentMethod] || invoice.paymentMethod || "Pago";

  return (
    <motion.article
      className="st-ticket"
      initial={reduce ? false : { opacity: 0, y: 8, scale: 0.98 }}
      animate={reduce ? undefined : { opacity: 1, y: 0, scale: 1 }}
      transition={{ duration: 0.2, delay: Math.min(index * 0.035, 0.18) }}
      layout
    >
      <header className="st-ticket-head">
        <div>
          <p>{invoice.fullNumber || "Ticket"}</p>
          <span>{timeLabel(invoice.date)}</span>
        </div>
        <strong>{money(invoice.total)}</strong>
      </header>
      <div className="st-ticket-method">
        <CreditCard size={14} />
        <span>{method}</span>
      </div>
      <ul className="st-ticket-items">
        {asArray(invoice.items).map((item, itemIndex) => (
          <li key={`${item.nameSnapshot}-${itemIndex}`}>
            <span>{Number(item.quantity || 0).toLocaleString("es-ES")}x {item.nameSnapshot}</span>
            <em>{money(item.total)}</em>
          </li>
        ))}
      </ul>
      <footer className="st-ticket-tax">
        <span>Base {money(invoice.subtotal)}</span>
        <span>IVA {money(invoice.taxAmount)}</span>
      </footer>
    </motion.article>
  );
}

function EmptyState({ type }) {
  const icon = type === "invoices" ? <CircleCheck size={20} />
    : type === "pending"  ? <Hourglass size={20} />
    : <Clock3 size={20} />;
  const text = type === "invoices" ? "Todavía no hay tickets cobrados."
    : type === "pending"  ? "Aún no has añadido nada al pedido."
    : "Todavía no hay pedidos en esta mesa.";
  return (
    <div className="st-empty">
      {icon}
      <p>{text}</p>
    </div>
  );
}

function PendingCartPanel({ cart, cartCount, cartTotal, onOpenCart }) {
  const items = asArray(cart);
  if (!items.length) return <EmptyState type="pending" />;
  return (
    <div className="st-pending">
      <motion.article
        className="st-pending-card"
        initial={{ opacity: 0, y: 8 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.22 }}
        layout
      >
        <header className="st-pending-head">
          <div>
            <p>Pedido pendiente de enviar</p>
            <h4>{cartCount} {cartCount === 1 ? "producto" : "productos"}</h4>
          </div>
          <strong>{money(cartTotal)}</strong>
        </header>
        <ul className="st-pending-items">
          {items.map((item) => (
            <li key={item.id}>
              <span>{Number(item.qty || 0).toLocaleString("es-ES")}x {item.name}</span>
              <em>{money(Number(item.price || 0) * Number(item.qty || 0))}</em>
            </li>
          ))}
        </ul>
        <motion.button
          type="button"
          className="st-pending-cta"
          onClick={onOpenCart}
          whileHover={{ scale: 1.02 }}
          whileTap={{ scale: 0.97 }}
        >
          <ShoppingCart size={16} />
          <span>Revisar y enviar pedido</span>
          <Send size={14} />
        </motion.button>
      </motion.article>
    </div>
  );
}

export default function SessionTracker({
  token,
  isOpen,
  onSessionClosed,
  cart = [],
  cartCount = 0,
  cartTotal = 0,
  onOpenCart
}) {
  const reduce = useReducedMotion();
  const [expanded, setExpanded] = useState(false);
  const [tab, setTab] = useState("pending");
  const [orders, setOrders] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [summary, setSummary] = useState(null);
  const [session, setSession] = useState(null);
  const [refreshing, setRefreshing] = useState(false);
  const [closed, setClosed] = useState(false);

  const applyOverview = useCallback((data) => {
    const nextOrders = asArray(data.orders).map(normalizeOrder);
    const nextInvoices = asArray(data.invoices).map(normalizeInvoice);
    setSession(data.session || null);
    setOrders(nextOrders);
    setInvoices(nextInvoices);
    setSummary(buildSummary(nextOrders, nextInvoices, data.summary || {}));
    setClosed(!data.session && data.status !== "open");
  }, []);

  const refreshOverview = useCallback(async () => {
    if (!token || !isOpen) return;
    setRefreshing(true);
    try {
      const res = await fetch(`/api/tables/token/${token}/session-overview`);
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || "No se pudo cargar el seguimiento");
      applyOverview(data);
    } catch (_) {
      // El socket volvera a sincronizar en el siguiente reconnect.
    } finally {
      setRefreshing(false);
    }
  }, [applyOverview, isOpen, token]);

  useEffect(() => {
    if (isOpen) refreshOverview();
  }, [isOpen, refreshOverview]);

  const socketState = useCustomerSocket(token, {
    onJoined: refreshOverview,
    onSessionOpened: refreshOverview,
    onOrderCreated: (payload) => {
      const nextOrder = normalizeOrder(payload);
      setOrders((prev) => {
        const next = upsertById(prev, nextOrder, getOrderId);
        setSummary((current) => buildSummary(next, invoices, current || {}));
        return next;
      });
    },
    onOrderStatusChanged: (payload) => {
      const nextOrder = normalizeOrder(payload);
      setOrders((prev) => {
        const next = upsertById(prev, nextOrder, getOrderId);
        setSummary((current) => buildSummary(next, invoices, current || {}));
        return next;
      });
    },
    onInvoiceIssued: (payload) => {
      const nextInvoice = normalizeInvoice(payload);
      setInvoices((prev) => {
        const next = upsertById(prev, nextInvoice, getInvoiceId);
        setSummary((current) => buildSummary(orders, next, current || {}));
        return next;
      });
      refreshOverview();
    },
    onInvoiceCancelled: (payload) => {
      const id = String(payload.invoiceId || payload._id || "");
      setInvoices((prev) => {
        const next = prev.filter((invoice) => getInvoiceId(invoice) !== id);
        setSummary((current) => buildSummary(orders, next, current || {}));
        return next;
      });
      refreshOverview();
    },
    onSessionClosed: () => {
      setClosed(true);
      setExpanded(true);
      window.setTimeout(() => onSessionClosed?.(), 3000);
    }
  });

  const liveOrders = useMemo(
    () => orders.filter((order) => !["paid", "cancelled"].includes(order.status)),
    [orders]
  );
  const paidOrders = useMemo(
    () => orders.filter((order) => order.status === "paid"),
    [orders]
  );
  const visibleOrders = liveOrders.length ? liveOrders : paidOrders;
  const totalOrders = summary?.ordersTotal || orders.length;
  const liveCount = summary?.ordersLive || liveOrders.length;
  const totalOrdered = summary?.totalOrdered || 0;

  if (!isOpen && !closed) return null;

  return (
    <motion.aside
      className={`st-footer${expanded ? " is-expanded" : ""}${closed ? " is-closed" : ""}`}
      initial={reduce ? false : { y: 80 }}
      animate={reduce ? undefined : { y: 0 }}
      transition={{ type: "spring", damping: 22, stiffness: 280 }}
      aria-live="polite"
    >
      <AnimatePresence>
        {expanded && (
          <motion.div
            className="st-backdrop"
            initial={reduce ? false : { opacity: 0 }}
            animate={reduce ? undefined : { opacity: 1 }}
            exit={reduce ? undefined : { opacity: 0 }}
            transition={{ duration: 0.18 }}
            onClick={() => setExpanded(false)}
          />
        )}
      </AnimatePresence>

      <button
        type="button"
        className="st-handle"
        onClick={() => setExpanded((value) => !value)}
        aria-expanded={expanded}
        aria-label={expanded ? "Cerrar seguimiento de pedidos" : "Abrir seguimiento de pedidos"}
      >
        <span className="st-handle-grip" aria-hidden="true" />
        <div className="st-handle-info">
          <ReceiptText size={17} aria-hidden="true" />
          <span className="st-handle-text">
            <strong>{totalOrders} pedidos</strong>
            <em>{money(totalOrdered)}</em>
          </span>
          {liveCount > 0 && <span className="st-handle-badge">{liveCount} en marcha</span>}
          {socketState !== "connected" && <WifiOff className="st-socket-icon" size={15} aria-label="Reconectando" />}
        </div>
        <ChevronUp className="st-handle-chevron" size={18} aria-hidden="true" />
      </button>

      <AnimatePresence>
        {expanded && (
          <motion.div
            className="st-sheet"
            initial={reduce ? false : { height: 0, opacity: 0 }}
            animate={reduce ? undefined : { height: "auto", opacity: 1 }}
            exit={reduce ? undefined : { height: 0, opacity: 0 }}
            transition={{ type: "spring", damping: 26, stiffness: 280 }}
          >
            <div className="st-sheet-inner">
              <header className="st-sheet-head">
                <div>
                  <h3>Mi mesa</h3>
                  <span>
                    {closed
                      ? "Sesión cerrada"
                      : `Sesión abierta · ${minutesAgo(summary?.sessionOpenedAt || session?.openedAt)}`}
                  </span>
                </div>
                <button className="st-icon-btn" type="button" onClick={() => setExpanded(false)} aria-label="Cerrar">
                  <X size={16} />
                </button>
                <button className="st-icon-btn" type="button" onClick={refreshOverview} aria-label="Refrescar">
                  <RefreshCw size={16} className={refreshing ? "is-spinning" : ""} />
                </button>
              </header>

              {closed && (
                <div className="st-closed-note">
                  <CircleCheck size={18} />
                  <span>La mesa se ha cerrado. El resumen se actualizará en unos segundos.</span>
                </div>
              )}

              <Tabs value={tab} onValueChange={setTab} className="st-tabs">
                <TabsList className="st-tabs-list" aria-label="Seguimiento de mesa">
                  <TabsTrigger value="pending" className="st-tabs-trigger">
                    Pendiente <span>{cartCount}</span>
                  </TabsTrigger>
                  <TabsTrigger value="orders" className="st-tabs-trigger">
                    En curso <span>{visibleOrders.length}</span>
                  </TabsTrigger>
                  <TabsTrigger value="invoices" className="st-tabs-trigger">
                    Pagado <span>{invoices.length}</span>
                  </TabsTrigger>
                </TabsList>
              </Tabs>

              {tab === "pending" && (
                <PendingCartPanel
                  cart={cart}
                  cartCount={cartCount}
                  cartTotal={cartTotal}
                  onOpenCart={() => {
                    setExpanded(false);
                    onOpenCart?.();
                  }}
                />
              )}

              {tab === "orders" && (
                <div className="st-list">
                  {visibleOrders.length ? (
                    visibleOrders.map((order, index) => (
                      <OrderTimelineCard key={getOrderId(order)} order={order} index={index} />
                    ))
                  ) : (
                    <EmptyState type="orders" />
                  )}
                </div>
              )}

              {tab === "invoices" && (
                <div className="st-list">
                  {invoices.length ? (
                    invoices.map((invoice, index) => (
                      <InvoiceTicket key={getInvoiceId(invoice)} invoice={invoice} index={index} />
                    ))
                  ) : (
                    <EmptyState type="invoices" />
                  )}
                </div>
              )}

              <footer className="st-sheet-foot">
                <span>Total mesa</span>
                <strong>{money(totalOrdered)}</strong>
              </footer>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.aside>
  );
}
